"use client";
import { Api } from "@/Apis";
import { CenterOptionSelect } from "@/Areas/Center/Components/Selects";
import {
  CenterOptionEnumSelect,
  FileUploader,
  If,
  LocalizedTextEditor,
  LocalizedTextInput,
} from "@/Components";
import { FileUploaderHandle } from "@/Components/FileUploader";
import { CenterOptionEnumSegmented } from "@/Components/Segmenteds";
import { Helper } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { ChildrenOf } from "@/Helpers/CenterOptions.types";
import { formProps } from "@/Helpers/Constants";
import { useAccount, useApp, useT } from "@/Hooks";
import useConstants from "@/Hooks/useConstants";
import { Button, Col, Flex, Form, Input, Row, Spin } from "antd";
import { FC, useEffect, useRef, useState } from "react";

export interface CenterSiteTemplatePageCreateOrEditPageForm {
  Id: number;
  Title: string;
  Path: string;
  StatusKey: ChildrenOf<"Status">;
  ForCenterTypeKey?: ChildrenOf<"CenterType">;
  Description?: string;
  ImageId?: number;
  CenterId?: number;
  CenterSiteTemplateId: number;
  CenterSiteTemplatePageTypeId: number;
}

export interface CenterSiteTemplatePageCreateOrEditPageProps {
  usageType: ChildrenOf<"CenterSiteTemplateUsageType">;
  values?: Partial<CenterSiteTemplatePageCreateOrEditPageForm>;
  callback: (result: boolean) => void;
}

const CenterSiteTemplatePageCreateOrEditPage: FC<
  CenterSiteTemplatePageCreateOrEditPageProps
> = ({ usageType, values, callback }) => {
  const { cancelButtonProps, saveButtonProps } = useConstants();
  const id = values?.Id;
  const [form] = Form.useForm<CenterSiteTemplatePageCreateOrEditPageForm>();
  const { message } = useApp();
  const { profile } = useAccount();
  const t = useT();
  const [loading, setLoading] = useState(true);
  const uploaderRef = useRef<FileUploaderHandle>(null);

  useEffect(() => {
    const load = async () => {
      if (id) {
        setLoading(true);
        try {
          let { Result } = await Api.CenterSiteTemplatePage.find({ Id: id });
          if (Result) form.setFieldsValue(Result);
        } catch (error) { }
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const onFinish = async (
    values: CenterSiteTemplatePageCreateOrEditPageForm,
  ) => {
    if (uploaderRef.current?.isUploading()) {
      message.warning(
        t(
          "common.message.waitForFileUploads",
          undefined,
          "Please wait until file uploads finish",
        ),
      );
      return;
    }
    setLoading(true);
    try {
      const r = id
        ? await Api.CenterSiteTemplatePage.edit(values)
        : await Api.CenterSiteTemplatePage.create(values);
      if (r.Success) {
        message.savedSuccessfully();
        callback(true);
      } else {
        message.errors(r.Errors);
      }
    } catch (error) {
      message.displayUnexpectedError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <Form
        {...formProps}
        onFinish={onFinish}
        form={form}
        initialValues={{
          StatusKey: CenterOptions.Enum.Status.Active.key,
          CenterSiteTemplateId:
            usageType ==
              CenterOptions.Enum.CenterSiteTemplateUsageType.Demo.key ||
              values?.CenterId
              ? undefined
              : profile?.DomainActiveCenterSiteTemplateId,
          ...values,
        }}
      >
        <Form.Item hidden name="Id">
          <Input />
        </Form.Item>
        <Form.Item hidden name="CenterId">
          <Input />
        </Form.Item>
        <Form.Item hidden name="CenterSiteTemplateId">
          <Input />
        </Form.Item>
        <Row>
          <Col md={8} xs={24}>
            <Form.Item
              name="CenterSiteTemplatePageTypeId"
              label={t("common.field.pageType", undefined, "Page Type")}
              rules={[{ required: true }]}
            >
              <CenterOptionSelect
                ParentKey={
                  CenterOptions.SiteTemplate.CenterSiteTemplatePageType.key
                }
                showCreateButton
                onSelect={async (v: number | undefined, o: any) => {
                  if (!id && v) {
                    // form.setFieldValue("Title", o?.RawText);
                    setLoading(true);
                    try {
                      let { Result } =
                        await Api.CenterSiteTemplatePage.getPathByPageTypeId({
                          CenterSiteTemplatePageTypeId: v,
                        });
                      if (Result) form.setFieldValue("Path", Result.Path);
                    } catch (error) { }
                    setLoading(false);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item
              name="ImageId"
              label={t("common.field.image", undefined, "Image")}
            >
              <FileUploader
                ref={uploaderRef}
                TableKey={CenterOptions.Table.CenterSiteTemplatePage.key}
                accept={`${Helper.imageAccept},.svg`}
              />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item
              name="Title"
              label={t("common.field.title", undefined, "Title")}
              rules={[{ required: true }]}
            >
              <LocalizedTextInput />
            </Form.Item>
          </Col>
          <If
            condition={
              usageType ==
              CenterOptions.Enum.CenterSiteTemplateUsageType.Demo.key
            }
          >
            <Col md={8} xs={24}>
              <Form.Item
                name="ForCenterTypeKey"
                label={t(
                  "center.siteTemplatePage.field.forCenters",
                  undefined,
                  "For Centers",
                )}
                rules={[{ required: true }]}
              >
                <CenterOptionEnumSelect
                  parentKey={CenterOptions.Enum.CenterType.key}
                />
              </Form.Item>
            </Col>
          </If>
          <Col md={8} xs={24}>
            <Form.Item
              name="StatusKey"
              label={t("common.field.status", undefined, "Status")}
              rules={[{ required: true }]}
            >
              <CenterOptionEnumSegmented
                parentKey={CenterOptions.Enum.Status.key}
              />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item
              name="Path"
              label={t("common.field.path", undefined, "Path")}
              rules={[{ required: true }]}
            >
              <Input className="ltr" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="Description"
              label={t("common.field.description", undefined, "Description")}
            >
              <LocalizedTextEditor />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex justify="end" gap={8}>
              <Button {...cancelButtonProps} onClick={() => callback(false)} />
              <Button {...saveButtonProps} htmlType="submit" />
            </Flex>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
};

export default CenterSiteTemplatePageCreateOrEditPage;
